/**
 * Live preview and contrast check for the admin Appearance tab.
 *
 * ContrastFloorTest holds the palette that ships in config to 4.5:1, and it
 * cannot see a runtime override an owner types into the admin. Those land in a
 * <style> tag on every storefront page, so without this an owner could push
 * the whole site under the floor with nothing saying so.
 *
 * The rules are not written here. They come from App\Support\Contrast and are
 * handed over as JSON, so this and the warning the controller writes on save
 * cannot drift apart. All this file does is evaluate them.
 */
export function registerThemePreview(Alpine) {
    Alpine.data('themePreview', (tokens = {}, rules = []) => ({
        c: { ...tokens },
        rules,

        get failures() {
            return this.rules.reduce((out, rule) => {
                const fg = this.resolve(rule.fg);
                const bg = this.resolve(rule.bg);

                if (!fg || !bg) return out;

                const ratio = Math.round(contrast(fg, bg) * 100) / 100;
                // `max` is for the pair that has to stay illegal: highlight is
                // a badge fill that takes dark text, never white.
                const broken = rule.min !== null ? ratio < rule.min : ratio >= rule.max;

                if (broken) out.push({ label: rule.label, ratio });

                return out;
            }, []);
        },

        resolve(ref) {
            const value = ref.startsWith('#') ? ref : this.c[ref];

            return /^#[0-9a-f]{6}$/i.test(value ?? '') ? value : null;
        },
    }));
}

function contrast(a, b) {
    const l1 = luminance(a);
    const l2 = luminance(b);

    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

function luminance(hex) {
    const ch = [1, 3, 5].map((i) => {
        const c = parseInt(hex.substr(i, 2), 16) / 255;

        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}
